import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Statistics, AdminInvoice, getAllRefunds } from "../../services/api";
import {
  FaUserPlus,
  FaDownload,
  FaHeadset,
  FaWaveSquare,
  FaUndo,
} from "react-icons/fa";

ChartJS.register(ArcElement, LineElement, PointElement, BarElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const RANGE_OPTIONS = [
  ["today", "Today"],
  ["week", "This Week"],
  ["month", "This Month"],
  ["year", "This Year"],
];

function withinRange(dateStr, range, customFrom, customTo) {
  const d = new Date(dateStr);
  const now = new Date();
  if (range === "today") {
    return d.toDateString() === now.toDateString();
  }
  if (range === "week") {
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    return d >= weekAgo && d <= now;
  }
  if (range === "month") {
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }
  if (range === "year") {
    return d.getFullYear() === now.getFullYear();
  }
  if (range === "custom") {
    if (!customFrom && !customTo) return true;
    const from = customFrom ? new Date(customFrom) : null;
    const to = customTo ? new Date(customTo) : null;
    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }
  return true;
}

export default function DashboardOverview({ Layout, canManage, basePath }) {
  const navigate = useNavigate();
  const [range, setRange] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  const { data: stats = {}, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["adminStatistics"],
    queryFn: Statistics,
    refetchInterval: 30000,
  });

  const { data: allInvoices = [], isLoading: invoicesLoading, error: invoicesError } = useQuery({
    queryKey: ["adminInvoices"],
    queryFn: () => AdminInvoice({}),
    refetchInterval: 30000,
  });

  const { data: refundsData } = useQuery({
    queryKey: ["dashboard-refunds"],
    queryFn: getAllRefunds,
    refetchInterval: 30000,
  });
  const pendingRefunds = (refundsData?.results || []).filter((r) => r.status === "pending");

  const invoices = useMemo(
    () => allInvoices.filter((inv) => {
      const s = (inv.status || "").toLowerCase().replace("paymentstatus.", "");
      const isCompleted = s === "completed" || s === "success" || s === "paid";
      return isCompleted && withinRange(inv.created_at || inv.date, range, customFrom, customTo);
    }),
    [allInvoices, range, customFrom, customTo],
  );

  const totalRevenue = useMemo(
    () => invoices.reduce((sum, inv) => sum + parseFloat(inv.invoice_amount || inv.amount || 0), 0),
    [invoices],
  );
  const totalTransactions = invoices.length;

  const paymentSplit = useMemo(() => {
    let quickPay = 0;
    let walletPay = 0;
    invoices.forEach((inv) => {
      const method = (inv.payment_method || "").toLowerCase();
      if (method.includes("quick") || method === "remita") quickPay++;
      else if (method.includes("wallet")) walletPay++;
    });
    const total = quickPay + walletPay || 1;
    return {
      quickPayCount: quickPay,
      walletPayCount: walletPay,
      quickPayPercent: Math.round((quickPay / total) * 100),
      walletPayPercent: Math.round((walletPay / total) * 100),
    };
  }, [invoices]);

  const paymentSplitData = {
    labels: ["Quick Pay", "Wallet Pay"],
    datasets: [{ data: [paymentSplit.quickPayPercent, paymentSplit.walletPayPercent], backgroundColor: ["#FFD60A", "#22C55E"], borderWidth: 0 }],
  };

  const completedInvoices = useMemo(() => allInvoices.filter((inv) => {
    const s = (inv.status || "").toLowerCase().replace("paymentstatus.", "");
    return s === "completed" || s === "success" || s === "paid";
  }), [allInvoices]);

  const monthlyData = useMemo(() => {
    const now = new Date();
    const monthLabels = [];
    const quickCounts = new Array(12).fill(0);
    const walletCounts = new Array(12).fill(0);
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthLabels.push(d.toLocaleString("default", { month: "short" }));
    }
    completedInvoices.forEach((inv) => {
      const invDate = new Date(inv.created_at || inv.date);
      const monthDiff = (now.getFullYear() - invDate.getFullYear()) * 12 + (now.getMonth() - invDate.getMonth());
      if (monthDiff >= 0 && monthDiff < 12) {
        const index = 11 - monthDiff;
        const method = (inv.payment_method || "").toLowerCase();
        if (method.includes("quick") || method === "remita" || method === "monnify" || method === "monnity") quickCounts[index]++;
        else if (method.includes("wallet")) walletCounts[index]++;
      }
    });
    return {
      labels: monthLabels,
      datasets: [
        { label: "Quick Pay", data: quickCounts, borderColor: "#FFD60A", backgroundColor: "#FFD60A33", tension: 0.4, fill: true },
        { label: "Wallet Pay", data: walletCounts, borderColor: "#22C55E", backgroundColor: "#22C55E33", tension: 0.4, fill: true },
      ],
    };
  }, [completedInvoices]);

  const serviceCharges = useMemo(() => {
    const now = new Date();
    let today = 0, month = 0, year = 0;
    // Count charges on ALL completed invoices (charges are only set on successful payments)
    allInvoices.forEach((inv) => {
      const s = (inv.status || "").toLowerCase().replace("paymentstatus.", "");
      if (s !== "completed" && s !== "success" && s !== "paid") return;
      const charge = parseFloat(inv.charges || 0);
      if (charge <= 0) return;
      const d = new Date(inv.created_at || inv.date);
      if (d.toDateString() === now.toDateString()) today += charge;
      if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) month += charge;
      if (d.getFullYear() === now.getFullYear()) year += charge;
    });
    return { today, month, year };
  }, [allInvoices]);

  const dailyRevenueData = useMemo(() => {
    const days = [];
    const totals = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      days.push(d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }));
      const dayTotal = completedInvoices
        .filter((inv) => new Date(inv.created_at || inv.date).toDateString() === d.toDateString())
        .reduce((s, inv) => s + parseFloat(inv.total_amount || inv.invoice_amount || 0), 0);
      totals.push(dayTotal);
    }
    return {
      labels: days,
      datasets: [{ label: "Revenue", data: totals, backgroundColor: "#0d3d2e", borderRadius: 4 }],
    };
  }, [allInvoices]);

  const revenueByService = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      (inv.items || []).forEach((item) => {
        const name = item?.service?.name || item?.name || "Other";
        const amt = parseFloat(item.line_total || item.amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [invoices]);

  const maxServiceRevenue = revenueByService[0]?.[1] || 1;
  const serviceColors = ["bg-green-600", "bg-blue-500", "bg-purple-500", "bg-orange-500", "bg-pink-500", "bg-gray-400"];

  const normalizeMethod = (method) => {
    if (!method) return "Unknown";
    const m = method.toLowerCase();
    if (m === "wallet") return "Wallet";
    return "QuickPay";
  };

  const normalizeChannel = (method) => {
    if (!method) return "—";
    const m = method.toLowerCase();
    if (m === "remita") return "Remita";
    if (m === "wallet") return "—";
    return "Monnify";
  };

  const amountColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return "text-green-600 font-semibold";
    if (s === "failed") return "text-red-500 font-semibold";
    return "text-yellow-600 font-semibold";
  };

  const recentTransactions = useMemo(() => {
    return invoices.slice(0, 10).map((inv, index) => {
      const meta = inv.customer_meta || {};
      return {
        id: inv.id,
        description: inv.items?.[0]?.service?.name || inv.description || "Invoice Payment",
        amount: parseFloat(inv.invoice_amount || inv.amount || 0),
        method: normalizeMethod(inv.payment_method),
        channel: normalizeChannel(inv.payment_method),
        ref: inv.reference || "—",
        patientName: `${meta.patient_first_name || ""} ${meta.patient_last_name || ""}`.trim() || "—",
        patientId: meta.patient_hospital_number || "—",
        patientEmail: meta.patient_email || "—",
        patientPhone: meta.patient_phone_number || "—",
        payerName: `${meta.payer_first_name || ""} ${meta.payer_last_name || ""}`.trim() || "—",
        payerEmail: meta.payer_email || "—",
        payerPhone: meta.payer_phone_number || "—",
        time: new Date(inv.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        date: new Date(inv.created_at).toLocaleDateString(),
        status: inv.status,
        raw: inv,
        index: index + 1,
      };
    });
  }, [invoices]);

  const chartOptions = { maintainAspectRatio: false, plugins: { legend: { position: "bottom" }, tooltip: { enabled: true } } };
  const barOptions = { maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } };

  const isLoading = statsLoading || invoicesLoading;
  const hasError = statsError || invoicesError;

  const handleExportCsv = () => {
    const header = ["Patient", "Patient ID", "Amount", "Method", "Reference", "Date"];
    const rows = recentTransactions.map((t) => [t.patientName, t.patientId, t.amount, t.method, t.ref, `${t.date} ${t.time}`]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions-report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
          <p className="text-gray-600 mt-1">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · Real-time platform performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportCsv} className="flex items-center gap-2 text-sm font-semibold text-gray-700 border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition bg-white">
            <FaDownload className="w-3 h-3" /> Export
          </button>
        </div>
      </div>

      {/* Date range tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {RANGE_OPTIONS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setRange(key); setShowCustom(false); }}
            className={`text-sm font-semibold px-4 py-2 rounded-xl transition ${
              range === key && !showCustom ? "bg-[#0d3d2e] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => { setShowCustom(!showCustom); setRange("custom"); }}
          className={`text-sm font-semibold px-4 py-2 rounded-xl transition flex items-center gap-1.5 ${
            showCustom ? "bg-[#0d3d2e] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
          }`}
        >
          📅 Custom range
        </button>
        {showCustom && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-sm border-0 focus:outline-none" />
            <span className="text-gray-400">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-sm border-0 focus:outline-none" />
          </div>
        )}
      </div>

      {pendingRefunds.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl px-5 py-3 mb-6 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-medium">
            ⚠️ {pendingRefunds.length} refund request{pendingRefunds.length !== 1 ? "s are" : " is"} awaiting your approval.
          </p>
          <button onClick={() => navigate(`${basePath}/refunds`)} className="text-sm font-semibold text-yellow-900 hover:underline">
            Review now →
          </button>
        </div>
      )}

      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">Error loading dashboard data. Please try again later.</div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500 text-lg">Loading dashboard...</div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] text-white p-6 rounded-xl shadow-sm">
              <p className="text-sm text-green-200 font-medium">Total Revenue Collected</p>
              <p className="text-3xl font-bold mt-2">₦{totalRevenue.toLocaleString()}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Transactions</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalTransactions.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-2">Wallet: {paymentSplit.walletPayCount} • Quick: {paymentSplit.quickPayCount}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Wallet Pay Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.wallet_users ?? stats.active_wallet_users ?? "—"}</p>
              <p className="text-xs text-gray-400 mt-2">Registered accounts</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Quick Pay Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.quick_pay_users ?? stats.active_quick_users ?? "—"}</p>
              <p className="text-xs text-gray-400 mt-2">Guest payments</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Deposits</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">₦{(stats.total_deposits || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-2">Wallet top-ups</p>
            </div>
          </div>

          {/* Service Charges — SuperAdmin only */}
          {canManage && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Service Charges Accumulated</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-xl p-5">
                  <p className="text-xs font-medium text-blue-600 mb-1">Today</p>
                  <p className="text-2xl font-bold text-blue-800">₦{serviceCharges.today.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-5">
                  <p className="text-xs font-medium text-purple-600 mb-1">This Month</p>
                  <p className="text-2xl font-bold text-purple-800">₦{serviceCharges.month.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-5">
                  <p className="text-xs font-medium text-amber-600 mb-1">This Year</p>
                  <p className="text-2xl font-bold text-amber-800">₦{serviceCharges.year.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>
          )}

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
              <h3 className="text-lg font-semibold mb-5">Monthly Transactions Trend</h3>
              <div className="h-64"><Line data={monthlyData} options={chartOptions} /></div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-1">Payment Distribution</h3>
              <p className="text-xs text-gray-400 mb-4">By payment method</p>
              <div className="h-44"><Doughnut data={paymentSplitData} options={chartOptions} /></div>
              <div className="space-y-2 mt-4 text-sm">
                <div className="flex items-center justify-between"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />Quick Pay revenue</span><strong>₦{invoices.filter(i=>normalizeMethod(i.payment_method)==="QuickPay").reduce((s,i)=>s+parseFloat(i.invoice_amount||i.amount||0),0).toLocaleString()}</strong></div>
                <div className="flex items-center justify-between"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-green-500" />Wallet Pay revenue</span><strong>₦{invoices.filter(i=>normalizeMethod(i.payment_method)==="Wallet Pay").reduce((s,i)=>s+parseFloat(i.invoice_amount||i.amount||0),0).toLocaleString()}</strong></div>
              </div>
            </div>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
              <h3 className="text-lg font-semibold mb-1">Daily Revenue</h3>
              <p className="text-xs text-gray-400 mb-4">Last 14 days — ₦ collected per day</p>
              <div className="h-56"><Bar data={dailyRevenueData} options={barOptions} /></div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-1">Revenue by Service</h3>
              <p className="text-xs text-gray-400 mb-4">Top categories this period</p>
              <div className="space-y-3">
                {revenueByService.length === 0 ? (
                  <p className="text-sm text-gray-400">No data for this period.</p>
                ) : (
                  revenueByService.map(([name, amt], idx) => (
                    <div key={name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{name}</span>
                        <span className="font-semibold text-gray-900">₦{amt.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${serviceColors[idx % serviceColors.length]}`} style={{ width: `${(amt / maxServiceRevenue) * 100}%` }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Recent Transactions + side panel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Recent Transactions</h3>
                <button onClick={() => navigate(`${basePath}/transactions`)} className="text-[#0d3d2e] hover:underline text-sm font-medium flex items-center gap-1">
                  View all →
                </button>
              </div>
              {recentTransactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No transactions found for this period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px] text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">#</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Patient</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Amount</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Method</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Channel</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Time</th>
                        <th className="text-left py-3 px-3 font-medium text-xs text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTransactions.map((tx) => (
                        <tr key={tx.id} className="border-b hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-3">{tx.index}</td>
                          <td className="py-3 px-3">
                            <p className="font-medium text-gray-900">{tx.patientName}</p>
                            <p className="text-xs text-gray-400">{tx.patientId}</p>
                          </td>
                          <td className={`py-3 px-3 ${amountColor(tx.status)}`}>₦{tx.amount.toLocaleString()}</td>
                          <td className="py-3 px-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${tx.method === "QuickPay" ? "bg-yellow-50 text-yellow-700" : "bg-green-50 text-green-700"}`}>{tx.method}</span>
                          </td>
                          <td className="py-3 px-3 text-gray-500 text-xs">{tx.channel}</td>
                          <td className="py-3 px-3 text-gray-500">{tx.time}</td>
                          <td className="py-3 px-3">
                            <button onClick={() => setSelectedTx(tx)} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors font-medium">View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] rounded-xl p-6 text-white">
                <p className="text-sm text-green-200 font-medium mb-1">Pending Refunds</p>
                <p className="text-4xl font-bold mb-1">{pendingRefunds.length}</p>
                <p className="text-xs text-green-200 mb-4">Requests awaiting approval</p>
                <button onClick={() => navigate(`${basePath}/refunds`)} className="w-full bg-white text-[#0d3d2e] font-semibold text-sm py-2.5 rounded-lg hover:bg-green-50 transition flex items-center justify-center gap-2">
                  Review requests →
                </button>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-1">
                  {canManage && (
                    <button onClick={() => navigate(`${basePath}/users`)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition text-left">
                      <FaUserPlus className="w-4 h-4 text-gray-400" />
                      <div><p className="font-medium text-gray-900">Add User</p><p className="text-xs text-gray-400">Create a new patient account</p></div>
                    </button>
                  )}
                  <button onClick={handleExportCsv} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition text-left">
                    <FaDownload className="w-4 h-4 text-gray-400" />
                    <div><p className="font-medium text-gray-900">Export Report</p><p className="text-xs text-gray-400">Download transaction CSV</p></div>
                  </button>
                  <button onClick={() => navigate(`${basePath}/support`)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition text-left">
                    <FaHeadset className="w-4 h-4 text-gray-400" />
                    <div><p className="font-medium text-gray-900">Support Tickets</p><p className="text-xs text-gray-400">View open tickets</p></div>
                  </button>
                  <button onClick={() => navigate(`${basePath}/activity-log`)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition text-left">
                    <FaWaveSquare className="w-4 h-4 text-gray-400" />
                    <div><p className="font-medium text-gray-900">Activity Log</p><p className="text-xs text-gray-400">View admin actions</p></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Transaction Details</DialogTitle></DialogHeader>
          {selectedTx && (
            <div className="py-3 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Patient Name</p><p className="font-medium">{selectedTx.patientName}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Patient ID</p><p className="font-medium">{selectedTx.patientId}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Patient Email</p><p className="font-medium">{selectedTx.patientEmail}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Patient Phone</p><p className="font-medium">{selectedTx.patientPhone}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Payer Name</p><p className="font-medium">{selectedTx.payerName}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Payer Email</p><p className="font-medium">{selectedTx.payerEmail}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Payer Phone</p><p className="font-medium">{selectedTx.payerPhone}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Invoice Amount</p><p className="text-xl font-bold">₦{selectedTx.amount.toLocaleString()}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Method</p><p className="font-medium">{selectedTx.method}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Channel</p><p className="font-medium">{selectedTx.channel}</p></div>
              </div>
              <div><p className="text-xs text-gray-400 mb-0.5">Description</p><p>{selectedTx.description}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 mb-0.5">Reference</p><p className="font-mono text-xs break-all">{selectedTx.ref}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Date & Time</p><p>{selectedTx.date} • {selectedTx.time}</p></div>
              </div>
              {selectedTx.raw?.items?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-1.5 font-medium">Services / Items</p>
                  <ul className="border rounded-lg divide-y text-sm bg-gray-50">
                    {selectedTx.raw.items.map((item, idx) => (
                      <li key={idx} className="flex justify-between px-3 py-2">
                        <span>{item.service?.name || "Unknown service"}</span>
                        <span className="font-semibold">₦{parseFloat(item.amount || 0).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSelectedTx(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
